// AuthIntro — intro animada del login (NEW_LOGIN).
// Reproduce una vez (al entrar) la secuencia de 5 frames del aula + logo
// entrando por la izquierda + particulas, y deja el panel de login (children)
// sobre la pizarra. No contiene logica de auth: solo el envoltorio visual.
import React, { useEffect, useRef } from "react";
import { logoEduca } from "../../../../assets";
import f1 from "../../../../assets/login/f1.jpg";
import f2 from "../../../../assets/login/f2.jpg";
import f3 from "../../../../assets/login/f3.jpg";
import f4 from "../../../../assets/login/f4.jpg";
import f5 from "../../../../assets/login/f5.jpg";
import f6 from "../../../../assets/login/f6.jpg";
import f7 from "../../../../assets/login/f7.jpg";
import f8 from "../../../../assets/login/f8.jpg";
import "./AuthIntro.scss";

const FRAMES = [f1, f2, f3, f4, f5];
const EXIT_FRAMES = [f6, f7, f8];
const COLORS = ["#16a934", "#FFD400", "#ffffff", "#00A75A"];

export function AuthIntro({ children, exiting = false }) {
  const stageRef = useRef(null);
  const canvasRef = useRef(null);

  // Arranca la intro una sola vez
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => stage.classList.add("go")),
    );
    const burst = setTimeout(() => {
      if (canvasRef.current && canvasRef.current._burst) {
        canvasRef.current._burst();
      }
    }, 2600);
    return () => {
      cancelAnimationFrame(id);
      clearTimeout(burst);
    };
  }, []);

  // Transicion de SALIDA (portal magico) tras login correcto
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !exiting) return;
    stage.classList.add("exit");
    if (canvasRef.current && canvasRef.current._burst) {
      canvasRef.current._burst();
      setTimeout(() => canvasRef.current && canvasRef.current._burst(), 550);
    }
  }, [exiting]);

  // Particulas (canvas vanilla, ligero)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    let W, H, raf;
    const resize = () => {
      W = canvas.width = canvas.offsetWidth || window.innerWidth;
      H = canvas.height = canvas.offsetHeight || window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const spawn = (init) => ({
      x: Math.random() * W,
      y: init ? Math.random() * H : H + 12,
      r: 2 + Math.random() * 5,
      vy: -(0.15 + Math.random() * 0.5),
      vx: (Math.random() - 0.5) * 0.25,
      a: 0.12 + Math.random() * 0.32,
      c: COLORS[(Math.random() * COLORS.length) | 0],
    });
    const amb = Array.from({ length: 34 }, () => spawn(true));
    let sparks = [];

    canvas._burst = () => {
      const cx = W * 0.5;
      const cy = H * 0.42;
      for (let i = 0; i < 70; i += 1) {
        const ang = Math.random() * Math.PI * 2;
        const sp = 1 + Math.random() * 4.5;
        sparks.push({
          x: cx,
          y: cy,
          vx: Math.cos(ang) * sp,
          vy: Math.sin(ang) * sp - 1.2,
          r: 1.5 + Math.random() * 2.5,
          life: 1,
          c: COLORS[(Math.random() * COLORS.length) | 0],
        });
      }
    };

    const loop = () => {
      ctx.clearRect(0, 0, W, H);
      for (const p of amb) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.y < -12) Object.assign(p, spawn(false));
        ctx.globalAlpha = p.a;
        ctx.fillStyle = p.c;
        ctx.shadowBlur = 12;
        ctx.shadowColor = p.c;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, 7);
        ctx.fill();
      }
      ctx.shadowBlur = 8;
      sparks = sparks.filter((s) => s.life > 0);
      for (const s of sparks) {
        s.vy += 0.06;
        s.x += s.vx;
        s.y += s.vy;
        s.life -= 0.012;
        ctx.globalAlpha = Math.max(0, s.life);
        ctx.fillStyle = s.c;
        ctx.shadowColor = s.c;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, 7);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      raf = requestAnimationFrame(loop);
    };
    loop();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div className="auth-intro" ref={stageRef}>
      <div className="ai-frames">
        {FRAMES.map((src, i) => (
          <img key={i} className={`ai-frame f${i + 1}`} src={src} alt="" />
        ))}
        {EXIT_FRAMES.map((src, i) => (
          <img
            key={`e${i}`}
            className={`ai-frame f${i + 6}`}
            src={src}
            alt=""
          />
        ))}
      </div>

      <div className="ai-logowrap">
        <img className="ai-logo" src={logoEduca.logoE360} alt="Educa360" />
      </div>

      <canvas className="ai-fx" ref={canvasRef} />

      <div className="ai-backdrop" />

      <div className="ai-card">{children}</div>

      <div className="ai-black" />
    </div>
  );
}
